<script setup lang="ts">
import { Check, Circle, Droplets, Eraser, Heart, Paintbrush, Pencil, RotateCcw, Send, Square, Triangle } from 'lucide-vue-next';
import { computed, nextTick, onMounted, ref } from 'vue';
import { useFamily } from '@/composables/useFamily';
import { useNavigation } from '@/composables/useNavigation';

type ToolKey = 'pencil' | 'brush' | 'watercolor' | 'acrylic' | 'eraser' | 'triangle' | 'rectangle' | 'circle';

interface Point {
  x: number;
  y: number;
}

interface Tool {
  key: ToolKey;
  label: string;
  icon: typeof Pencil;
}

const tools: Tool[] = [
  { key: 'pencil', label: 'Stift', icon: Pencil },
  { key: 'brush', label: 'Pinsel', icon: Paintbrush },
  { key: 'watercolor', label: 'Wasser', icon: Droplets },
  { key: 'acrylic', label: 'Acryl', icon: Paintbrush },
  { key: 'eraser', label: 'Radierer', icon: Eraser },
  { key: 'triangle', label: 'Dreieck', icon: Triangle },
  { key: 'rectangle', label: 'Viereck', icon: Square },
  { key: 'circle', label: 'Kreis', icon: Circle }
];

const colors = ['#ef4444', '#f97316', '#facc15', '#22c55e', '#38bdf8', '#6366f1', '#a855f7', '#ec4899', '#334155', '#ffffff'];

const { addPendingDrawing, drawingOccasion, selectedFriend } = useFamily();
const { navigate } = useNavigation();

const canvas = ref<HTMLCanvasElement | null>(null);
const selectedTool = ref<ToolKey>('pencil');
const selectedColor = ref(colors[0]);
const brushSize = ref(14);
const isDrawing = ref(false);
const startPoint = ref<Point | null>(null);
const lastPoint = ref<Point | null>(null);
const shapeSnapshot = ref<ImageData | null>(null);
const savedNotice = ref(false);
const sentDrawingUrl = ref('');

const friendName = computed(() => selectedFriend.value?.displayName ?? 'Freund');
const drawingTitle = computed(() =>
  drawingOccasion.value === 'birthday' ? `Geburtstagsbild fuer ${friendName.value}` : `Bild fuer ${friendName.value}`
);
const isShapeTool = computed(() => ['triangle', 'rectangle', 'circle'].includes(selectedTool.value));
const previewSize = computed(() => `${Math.max(10, brushSize.value * 1.4)}px`);

onMounted(async () => {
  await nextTick();
  resizeCanvas();
  window.addEventListener('resize', resizeCanvas);
});

function resizeCanvas() {
  const element = canvas.value;
  if (!element) return;

  const rect = element.getBoundingClientRect();
  const ratio = window.devicePixelRatio || 1;
  const previous = document.createElement('canvas');
  previous.width = element.width;
  previous.height = element.height;
  previous.getContext('2d')?.drawImage(element, 0, 0);

  element.width = Math.max(1, Math.floor(rect.width * ratio));
  element.height = Math.max(1, Math.floor(rect.height * ratio));

  const context = element.getContext('2d');
  if (!context) return;

  context.scale(ratio, ratio);
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, rect.width, rect.height);

  if (previous.width > 0 && previous.height > 0) {
    context.drawImage(previous, 0, 0, rect.width, rect.height);
  }
}

function getPoint(event: PointerEvent): Point {
  const element = canvas.value;
  if (!element) return { x: 0, y: 0 };
  const rect = element.getBoundingClientRect();
  return {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top
  };
}

function beginDrawing(event: PointerEvent) {
  const element = canvas.value;
  const context = element?.getContext('2d');
  if (!element || !context) return;

  element.setPointerCapture(event.pointerId);
  isDrawing.value = true;
  savedNotice.value = false;
  const point = getPoint(event);
  startPoint.value = point;
  lastPoint.value = point;
  applyToolStyle(context);

  if (isShapeTool.value) {
    shapeSnapshot.value = context.getImageData(0, 0, element.width, element.height);
    return;
  }

  context.beginPath();
  context.moveTo(point.x, point.y);
}

function continueDrawing(event: PointerEvent) {
  if (!isDrawing.value) return;
  const element = canvas.value;
  const context = element?.getContext('2d');
  const from = startPoint.value;
  if (!element || !context || !from) return;

  const point = getPoint(event);
  applyToolStyle(context);

  if (isShapeTool.value) {
    if (shapeSnapshot.value) context.putImageData(shapeSnapshot.value, 0, 0);
    drawShape(context, from, point);
    return;
  }

  if (selectedTool.value === 'watercolor' && lastPoint.value) {
    drawWatercolorStroke(context, lastPoint.value, point);
    lastPoint.value = point;
    return;
  }

  context.lineTo(point.x, point.y);
  context.stroke();
  lastPoint.value = point;
}

function finishDrawing(event: PointerEvent) {
  if (!isDrawing.value) return;
  continueDrawing(event);
  isDrawing.value = false;
  startPoint.value = null;
  lastPoint.value = null;
  shapeSnapshot.value = null;
}

function applyToolStyle(context: CanvasRenderingContext2D) {
  context.globalCompositeOperation = selectedTool.value === 'eraser' ? 'destination-out' : 'source-over';
  context.strokeStyle = selectedColor.value;
  context.fillStyle = selectedColor.value;
  context.globalAlpha = selectedTool.value === 'watercolor' ? 0.28 : 1;

  if (selectedTool.value === 'pencil') context.lineWidth = Math.max(2, brushSize.value * 0.45);
  if (selectedTool.value === 'brush') context.lineWidth = brushSize.value;
  if (selectedTool.value === 'watercolor') context.lineWidth = brushSize.value * 1.4;
  if (selectedTool.value === 'acrylic') context.lineWidth = brushSize.value * 1.15;
  if (selectedTool.value === 'eraser') context.lineWidth = brushSize.value * 1.8;
  if (isShapeTool.value) context.lineWidth = Math.max(3, brushSize.value * 0.75);
}

function drawWatercolorStroke(context: CanvasRenderingContext2D, from: Point, to: Point) {
  context.save();
  context.globalCompositeOperation = 'source-over';
  context.strokeStyle = selectedColor.value;
  context.lineCap = 'round';
  context.lineJoin = 'round';

  const layers = [
    { width: brushSize.value * 1.75, alpha: 0.12, offsetX: -1.8, offsetY: 1.2 },
    { width: brushSize.value * 1.35, alpha: 0.18, offsetX: 1.4, offsetY: -1.6 },
    { width: brushSize.value * 0.95, alpha: 0.22, offsetX: 0, offsetY: 0 }
  ];

  for (const layer of layers) {
    context.globalAlpha = layer.alpha;
    context.lineWidth = layer.width;
    context.beginPath();
    context.moveTo(from.x + layer.offsetX, from.y + layer.offsetY);
    context.lineTo(to.x + layer.offsetX, to.y + layer.offsetY);
    context.stroke();
  }

  const distance = Math.hypot(to.x - from.x, to.y - from.y);
  const blooms = Math.max(1, Math.floor(distance / 18));
  context.fillStyle = selectedColor.value;
  context.globalAlpha = 0.08;

  for (let index = 0; index < blooms; index += 1) {
    const progress = (index + 1) / (blooms + 1);
    const wobble = Math.sin((from.x + from.y + index * 23) * 0.08) * brushSize.value * 0.18;
    const x = from.x + (to.x - from.x) * progress + wobble;
    const y = from.y + (to.y - from.y) * progress - wobble;
    context.beginPath();
    context.ellipse(x, y, brushSize.value * 0.55, brushSize.value * 0.34, wobble, 0, Math.PI * 2);
    context.fill();
  }

  context.restore();
}

function drawShape(context: CanvasRenderingContext2D, from: Point, to: Point) {
  context.beginPath();

  if (selectedTool.value === 'rectangle') {
    context.rect(from.x, from.y, to.x - from.x, to.y - from.y);
  }

  if (selectedTool.value === 'circle') {
    const radius = Math.hypot(to.x - from.x, to.y - from.y);
    context.arc(from.x, from.y, radius, 0, Math.PI * 2);
  }

  if (selectedTool.value === 'triangle') {
    context.moveTo((from.x + to.x) / 2, from.y);
    context.lineTo(to.x, to.y);
    context.lineTo(from.x, to.y);
    context.closePath();
  }

  context.stroke();
}

function clearCanvas() {
  const element = canvas.value;
  const context = element?.getContext('2d');
  if (!element || !context) return;
  const rect = element.getBoundingClientRect();
  context.globalCompositeOperation = 'source-over';
  context.globalAlpha = 1;
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, rect.width, rect.height);
  savedNotice.value = false;
  sentDrawingUrl.value = '';
}

async function finishPicture() {
  const element = canvas.value;
  const friend = selectedFriend.value;
  if (!element || !friend || savedNotice.value) return;

  const drawingUrl = element.toDataURL('image/png');
  const drawingBlob = await canvasToBlob(element);
  await addPendingDrawing(friend.friendChildId, drawingBlob);
  sentDrawingUrl.value = drawingUrl;
  savedNotice.value = true;
  playSuccessSound();
}

function canvasToBlob(element: HTMLCanvasElement) {
  return new Promise<Blob>((resolve) => {
    element.toBlob((blob) => {
      resolve(blob ?? dataUrlToBlob(element.toDataURL('image/png')));
    }, 'image/png');
  });
}

function dataUrlToBlob(dataUrl: string) {
  const [header, base64] = dataUrl.split(',');
  const mimeType = header.match(/data:(.*);base64/)?.[1] ?? 'image/png';
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: mimeType });
}

function returnToFriend() {
  savedNotice.value = false;
  sentDrawingUrl.value = '';
  navigate('friend');
}

function playSuccessSound() {
  if (typeof window === 'undefined') return;
  const AudioContextConstructor =
    window.AudioContext ??
    (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

  if (!AudioContextConstructor) {
    void new Audio(createSuccessToneDataUrl()).play().catch(() => undefined);
    return;
  }

  const audioContext = new AudioContextConstructor();
  const masterGain = audioContext.createGain();
  masterGain.gain.setValueAtTime(0.0001, audioContext.currentTime);
  masterGain.gain.exponentialRampToValueAtTime(0.09, audioContext.currentTime + 0.03);
  masterGain.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.62);
  masterGain.connect(audioContext.destination);

  const notes = [
    { frequency: 523.25, start: 0 },
    { frequency: 659.25, start: 0.12 },
    { frequency: 783.99, start: 0.25 }
  ];

  for (const note of notes) {
    const oscillator = audioContext.createOscillator();
    const noteGain = audioContext.createGain();
    const start = audioContext.currentTime + note.start;
    const end = start + 0.26;

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(note.frequency, start);
    noteGain.gain.setValueAtTime(0.0001, start);
    noteGain.gain.exponentialRampToValueAtTime(0.7, start + 0.025);
    noteGain.gain.exponentialRampToValueAtTime(0.0001, end);
    oscillator.connect(noteGain);
    noteGain.connect(masterGain);
    oscillator.start(start);
    oscillator.stop(end + 0.02);
  }

  window.setTimeout(() => void audioContext.close(), 850);
}

function createSuccessToneDataUrl() {
  const sampleRate = 22050;
  const duration = 0.7;
  const sampleCount = Math.floor(sampleRate * duration);
  const dataSize = sampleCount * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const notes = [523.25, 659.25, 783.99];

  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeAscii(view, 8, 'WAVE');
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  for (let index = 0; index < sampleCount; index += 1) {
    const time = index / sampleRate;
    const noteIndex = Math.min(notes.length - 1, Math.floor(time / 0.18));
    const phase = Math.sin(Math.PI * 2 * notes[noteIndex] * time);
    const attack = Math.min(1, time / 0.04);
    const release = Math.max(0, 1 - Math.max(0, time - 0.48) / 0.22);
    const sample = Math.round(phase * 0.22 * attack * release * 32767);
    view.setInt16(44 + index * 2, sample, true);
  }

  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return `data:audio/wav;base64,${window.btoa(binary)}`;
}

function writeAscii(view: DataView, offset: number, text: string) {
  for (let index = 0; index < text.length; index += 1) {
    view.setUint8(offset + index, text.charCodeAt(index));
  }
}
</script>

<template>
  <section class="relative mx-auto max-w-7xl px-4 py-5 pb-28 sm:px-6 md:pb-8">
    <div v-if="savedNotice" class="fixed inset-0 z-40 grid place-items-center bg-heart-ink/35 px-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="drawing-sent-title">
      <div class="sent-dialog w-full max-w-md rounded-[34px] bg-white p-6 text-center shadow-soft md:p-8">
        <div class="mx-auto grid h-24 w-24 place-items-center rounded-full bg-heart-mint text-heart-leaf">
          <Check class="h-14 w-14" aria-hidden="true" />
        </div>
        <h2 id="drawing-sent-title" class="mt-5 text-4xl font-black text-heart-berry">Bild verschickt!</h2>
        <div class="sent-animation-stage mt-5" aria-hidden="true">
          <div class="sent-cloud sent-cloud-a" />
          <div class="sent-cloud sent-cloud-b" />
          <div class="sent-path" />
          <div class="sent-mini-drawing">
            <img v-if="sentDrawingUrl" :src="sentDrawingUrl" alt="" />
          </div>
          <div class="sent-friend-house">
            <div class="sent-house-roof" />
            <div class="sent-house-body">
              <span>{{ friendName.slice(0, 1) }}</span>
            </div>
          </div>
          <Heart class="sent-heart sent-heart-a fill-heart-coral" aria-hidden="true" />
          <Heart class="sent-heart sent-heart-b fill-heart-coral" aria-hidden="true" />
        </div>
        <p class="mt-3 text-xl font-black leading-8 text-heart-ink">
          Mama oder Papa schauen kurz drauf.
        </p>
        <button
          class="focus-ring mt-7 inline-flex min-h-20 w-full items-center justify-center rounded-[26px] bg-heart-leaf px-6 text-2xl font-black text-white shadow-soft transition hover:-translate-y-1 active:scale-[0.98]"
          type="button"
          @click="returnToFriend"
        >
          Zurueck zu {{ friendName }}
        </button>
      </div>
    </div>

    <div class="mb-5 flex flex-wrap items-center justify-between gap-3">
      <button class="focus-ring rounded-full bg-white px-5 py-3 font-black text-heart-ink shadow-sm" type="button" @click="navigate('friend')">
        Zurueck
      </button>
      <h1 class="text-3xl font-black text-heart-berry md:text-5xl">
        {{ drawingTitle }}
      </h1>
    </div>

    <div class="grid gap-5 lg:grid-cols-[minmax(0,1fr)_21rem]">
      <div class="rounded-[36px] bg-amber-900 p-3 shadow-soft md:p-5">
        <div class="rounded-[28px] bg-orange-100 p-3 md:p-5">
          <div class="rounded-[22px] border-[10px] border-white bg-white shadow-inner md:border-[16px]">
            <canvas
              ref="canvas"
              class="block aspect-[4/3] w-full touch-none rounded-xl bg-white"
              aria-label="Malbereich"
              @pointerdown="beginDrawing"
              @pointermove="continueDrawing"
              @pointerup="finishDrawing"
              @pointercancel="finishDrawing"
              @pointerleave="finishDrawing"
            />
          </div>
        </div>
      </div>

      <aside class="grid grid-cols-[minmax(0,1fr)_4.5rem] gap-3 rounded-[28px] bg-white p-4 shadow-soft lg:sticky lg:top-24 lg:self-start" aria-label="Werkzeuge">
        <div>
          <div class="grid grid-cols-4 gap-2 lg:grid-cols-2">
            <button
              v-for="tool in tools"
              :key="tool.key"
              class="focus-ring flex min-h-20 flex-col items-center justify-center gap-2 rounded-2xl border-2 px-2 text-sm font-black"
              :class="selectedTool === tool.key ? 'border-heart-coral bg-rose-50 text-heart-berry' : 'border-orange-100 bg-heart-cream text-heart-ink'"
              type="button"
              :aria-label="tool.label"
              @click="selectedTool = tool.key"
            >
              <component :is="tool.icon" class="h-7 w-7" aria-hidden="true" />
              {{ tool.label }}
            </button>
          </div>

          <div class="mt-5 grid grid-cols-5 gap-2">
            <button
              v-for="color in colors"
              :key="color"
              class="focus-ring h-12 rounded-full border-4"
              :class="selectedColor === color ? 'border-heart-coral' : 'border-white'"
              :style="{ backgroundColor: color }"
              type="button"
              aria-label="Farbe waehlen"
              @click="selectedColor = color"
            />
          </div>

          <div class="mt-5 grid gap-3">
            <button class="focus-ring inline-flex min-h-14 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 font-black text-heart-ink" type="button" @click="clearCanvas">
              <RotateCcw class="h-5 w-5" aria-hidden="true" />
              Neu
            </button>
            <button
              class="focus-ring group relative flex min-h-24 w-full items-center justify-center overflow-hidden rounded-[28px] bg-heart-coral px-5 py-4 text-center text-2xl font-black leading-tight text-white shadow-soft transition hover:-translate-y-1 active:scale-[0.98] disabled:translate-y-0 disabled:opacity-80"
              type="button"
              :disabled="savedNotice"
              @click="finishPicture"
            >
              <span class="absolute inset-x-5 top-3 h-2 rounded-full bg-white/30" aria-hidden="true" />
              <span class="inline-flex items-center justify-center gap-3">
                <Send class="h-9 w-9 transition group-hover:translate-x-1 group-hover:-translate-y-1" aria-hidden="true" />
                Bild schicken
              </span>
            </button>
          </div>
        </div>

        <div class="flex min-h-[28rem] flex-col items-center rounded-3xl bg-heart-cream px-2 py-4">
          <div class="mb-3 grid h-12 w-12 place-items-center rounded-full bg-white shadow-sm">
            <span class="rounded-full bg-heart-coral" :style="{ width: previewSize, height: previewSize }" />
          </div>
          <input
            v-model.number="brushSize"
            class="drawing-size-slider focus-ring flex-1"
            type="range"
            min="4"
            max="34"
            step="1"
            aria-label="Stiftdicke einstellen"
          />
          <span class="mt-3 rounded-full bg-white px-3 py-2 text-sm font-black text-heart-ink">{{ brushSize }}</span>
        </div>
      </aside>
    </div>
  </section>
</template>

<style scoped>
.sent-dialog {
  animation: sent-dialog-pop 220ms ease-out both;
}

.sent-animation-stage {
  position: relative;
  height: 10.5rem;
  overflow: hidden;
  border-radius: 1.6rem;
  background:
    linear-gradient(180deg, #dff6ff 0%, #fff7ed 64%, #dcfce7 100%);
}

.sent-cloud,
.sent-cloud::before,
.sent-cloud::after {
  position: absolute;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.74);
}

.sent-cloud {
  width: 5.2rem;
  height: 1.8rem;
}

.sent-cloud::before,
.sent-cloud::after {
  content: "";
}

.sent-cloud::before {
  left: 0.8rem;
  top: -0.7rem;
  width: 2rem;
  height: 2rem;
}

.sent-cloud::after {
  right: 0.7rem;
  top: -0.45rem;
  width: 1.55rem;
  height: 1.55rem;
}

.sent-cloud-a {
  left: 1.1rem;
  top: 1.1rem;
}

.sent-cloud-b {
  right: 4.2rem;
  top: 1.8rem;
  transform: scale(0.8);
}

.sent-path {
  position: absolute;
  left: 4.4rem;
  top: 5.3rem;
  width: 12.8rem;
  height: 4.5rem;
  border-top: 0.24rem dashed rgba(251, 113, 133, 0.55);
  border-radius: 50%;
  transform: rotate(-14deg);
  transform-origin: left center;
  animation: sent-path-draw 1.7s ease-out both;
}

.sent-mini-drawing {
  position: absolute;
  left: 2rem;
  top: 5.2rem;
  z-index: 2;
  width: 4.5rem;
  height: 3.3rem;
  overflow: hidden;
  border: 0.22rem solid #ffffff;
  border-radius: 0.55rem;
  background: #ffffff;
  box-shadow: 0 12px 24px rgba(190, 18, 60, 0.16);
  animation: sent-picture-to-house 1.85s cubic-bezier(0.22, 0.75, 0.22, 1) both;
}

.sent-mini-drawing img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sent-friend-house {
  position: absolute;
  right: 2rem;
  bottom: 1.2rem;
  width: 5.5rem;
  height: 5rem;
  filter: drop-shadow(0 12px 18px rgba(14, 165, 233, 0.18));
  animation: sent-house-ready 1.85s ease-out both;
}

.sent-house-roof {
  position: absolute;
  left: 50%;
  top: 0;
  width: 3.9rem;
  height: 3.9rem;
  border-radius: 0.85rem;
  background: #fb7185;
  transform: translateX(-50%) rotate(45deg);
}

.sent-house-body {
  position: absolute;
  left: 50%;
  bottom: 0;
  display: grid;
  width: 4.7rem;
  height: 3.55rem;
  place-items: center;
  border: 0.22rem solid #ffffff;
  border-radius: 0.9rem;
  background: #bae6fd;
  color: #0369a1;
  font-size: 1.6rem;
  font-weight: 900;
  transform: translateX(-50%);
}

.sent-heart {
  position: absolute;
  z-index: 3;
  color: #fb7185;
  font-size: 1.45rem;
  font-weight: 900;
  opacity: 0;
  animation: sent-heart-pop 1.85s ease-out both;
}

.sent-heart-a {
  right: 5.8rem;
  top: 2.75rem;
}

.sent-heart-b {
  right: 3.3rem;
  top: 2.1rem;
  animation-delay: 0.14s;
}

@keyframes sent-dialog-pop {
  0% {
    opacity: 0;
    transform: translateY(0.8rem) scale(0.94);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes sent-picture-to-house {
  0% {
    transform: translate3d(0, 0, 0) rotate(-4deg) scale(1);
  }
  58% {
    opacity: 1;
    transform: translate3d(9.5rem, -3rem, 0) rotate(8deg) scale(0.72);
  }
  100% {
    opacity: 0;
    transform: translate3d(14.2rem, -1.35rem, 0) rotate(0deg) scale(0.34);
  }
}

@keyframes sent-house-ready {
  0%,
  55% {
    transform: scale(1);
  }
  72% {
    transform: scale(1.08);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes sent-path-draw {
  0% {
    opacity: 0;
    clip-path: inset(0 100% 0 0);
  }
  18%,
  80% {
    opacity: 1;
  }
  100% {
    opacity: 0.2;
    clip-path: inset(0 0 0 0);
  }
}

@keyframes sent-heart-pop {
  0%,
  64% {
    opacity: 0;
    transform: translateY(0.4rem) scale(0.4);
  }
  76% {
    opacity: 1;
    transform: translateY(-0.1rem) scale(1.1);
  }
  100% {
    opacity: 0.92;
    transform: translateY(-0.5rem) scale(1);
  }
}
</style>
